// ============================================================================
// IMPORTS
// ============================================================================
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { 
  getConversations as getLivechatConversations, 
  getMessages as getLivechatMessages,
  markAsRead as markLivechatAsRead,
  getLivechatAccessInfo,
  getStatuses as getLivechatStatuses,
} from "../apis/livechat";
import { getSocket } from "../utils/socket";
import {
  SOCKET_EVENTS,
  SOCKET_CLIENT_EVENTS,
  CHAT_JOIN_MODE,
} from "../constants/liveChatSocket";
import {
  normalizePhoneId,
  customersMatch,
  phonesMatch,
} from "../utils/liveChatRealtime";
import { chatRoom, parseChatRoomId } from "../utils/liveChatRooms";
import {
  formatTimestampForDisplayUTC,
  getTimestampForSorting,
  normalizeTimestampToISO,
} from "../utils/timestampUtils";
import { usePhoneAccount } from "./PhoneAccountContext";

// ============================================================================
// CONTEXT CREATION
// ============================================================================
const LiveChatContext = createContext(null);

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format last message text for display in conversation list
 * Handles: text, media (with caption), button/interactive messages
 */
const formatLastMessageText = (lastMessage) => {
  if (!lastMessage) return "No messages";

  // Template messages
  if (lastMessage.message_type === "template") {
    const name = lastMessage.template_name || lastMessage.template_data?.template_name;
    return name ? `📋 Template: ${name}` : "📋 Template message";
  }
  
  // Media messages (image, video, audio, document)
  if (lastMessage.media_url || ["image", "video", "audio", "document"].includes(lastMessage.message_type)) {
    const mediaType = lastMessage.message_type || "media";
    const mediaTypeLabels = {
      image: "📷 Image",
      video: "🎥 Video",
      audio: "🎵 Audio",
      document: "📄 Document",
      sticker: "🎨 Sticker",
    };
    let text = mediaTypeLabels[mediaType] || "📎 Media";
    if (lastMessage.caption) {
      text += `: ${lastMessage.caption}`;
    } else if (lastMessage.message_text) {
      text += `: ${lastMessage.message_text}`;
    }
    return text;
  }
  
  // Interactive messages (outbound with raw_payload)
  if (lastMessage.message_type === "interactive") {
    const raw = lastMessage.raw_payload;
    if (raw?.interactive?.body?.text) {
      const body = raw.interactive.body.text;
      return body.length > 60 ? body.substring(0, 57) + "..." : body;
    }
    if (lastMessage.button?.text) return `🔘 ${lastMessage.button.text}`;
    if (lastMessage.button?.payload) return `🔘 ${lastMessage.button.payload}`;
    if (lastMessage.message_text) return `🔘 ${lastMessage.message_text}`;
    return "🔘 Interactive message";
  }

  // Button messages
  if (lastMessage.message_type === "button") {
    if (lastMessage.button?.text) return `🔘 ${lastMessage.button.text}`;
    if (lastMessage.button?.payload) return `🔘 ${lastMessage.button.payload}`;
    if (lastMessage.message_text) return `🔘 ${lastMessage.message_text}`;
    return "🔘 Button Interaction";
  }
  
  // Text messages
  return lastMessage.message_text || "No messages";
};

/**
 * Extract customer phone from message data
 * Uses customer_wa_id if available, otherwise infers from direction
 */
const getCustomerPhone = (messageData) => {
  return messageData.customer_wa_id || 
    (messageData.direction === "inbound" 
      ? messageData.from_wa_id 
      : messageData.to_wa_id);
};

const MESSAGES_PAGE_SIZE = 50;

function messageDedupeKey(msg) {
  return msg.message_id || msg.id || `${msg.sent_at}_${msg.direction}`;
}

function mergeAndSortMessages(dbMessages, realtimeMessages = []) {
  const messagesMap = new Map();

  // Realtime first, then DB overwrites — after a fetch, signed URLs from the
  // API must win over stale socket payloads (unsigned / short-lived signed).
  realtimeMessages.forEach((msg) => {
    const key = messageDedupeKey(msg);
    if (key) messagesMap.set(key, msg);
  });

  dbMessages.forEach((msg) => {
    const key = messageDedupeKey(msg);
    if (key) messagesMap.set(key, msg);
  });

  return Array.from(messagesMap.values())
    .map((msg) => ({
      ...msg,
      sent_at: normalizeTimestampToISO(msg.sent_at) || msg.sent_at,
    }))
    .sort((a, b) => {
      const timeA = getTimestampForSorting(a.sent_at || a.timestamp);
      const timeB = getTimestampForSorting(b.sent_at || b.timestamp);
      return timeA - timeB;
    });
}

// ============================================================================
// MAIN PROVIDER COMPONENT
// ============================================================================

export const LiveChatProvider = ({ children }) => {
  // ========================================================================
  // SECTION 1: STATE MANAGEMENT
  // ========================================================================
  const { selectedPhoneId } = usePhoneAccount();
  
  const [chats, setChats] = useState([]);              // Conversation list
  const [selectedChat, setSelectedChat] = useState(null); // Currently selected conversation
  const [messages, setMessages] = useState([]);        // Messages for selected chat
  const [loading, setLoading] = useState(false);      // Loading conversations
  const [messagesLoading, setMessagesLoading] = useState(false); // Loading messages
  const [messagesLoadingMore, setMessagesLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [messagesTotal, setMessagesTotal] = useState(0);
  const [accessDenied, setAccessDenied] = useState(false); // Access denied flag
  const [chatsTotal, setChatsTotal] = useState(0);   // Total count for pagination
  const [chatsPage, setChatsPage] = useState(1);       // Current page (1-based)
  const [chatsWindowCounts, setChatsWindowCounts] = useState({ active: 0, non_active: 0 });
  const [chatStatusesList, setChatStatusesList] = useState([]); // Loaded once on mount
  // Message staged for quoted reply. Holds the full message object so
  // MessageInput can render a preview ("Replying to …") and the send
  // handler can forward message_id to the API.
  const [replyingTo, setReplyingTo] = useState(null);
  const [filters, setFilters] = useState({
    agent_ids: [],
    status_ids: [],
    date_from: "",
    date_to: "",
  });
  const CHATS_PAGE_SIZE = 50;
  const messagesOffsetRef = useRef(0);
  const hasMoreMessagesRef = useRef(false);
  const loadingMoreMessagesRef = useRef(false);

  // ========================================================================
  // SECTION 2: REFS (for stable references across renders)
  // ========================================================================
  const realtimeMessagesRef = useRef(new Map());       // Cache: real-time messages not yet in DB
  const selectedChatRef = useRef(null);                // Current selected chat (for socket listeners)
  const processedMessageIdsRef = useRef(new Set());    // Deduplication: processed message IDs
  const livechatRoomJoinedRef = useRef(false);
  const joinedUserRoomRef = useRef(null);
  const joinedChatRoomsRef = useRef(new Map());
  const chatsPageRef = useRef(1);                      // Current page for refetch (no-arg) use
  const filtersRef = useRef({ agent_ids: [], status_ids: [], date_from: "", date_to: "" });
  const selectedPhoneIdRef = useRef(selectedPhoneId);  // Always-current phoneId for socket handlers (avoids stale closure)
  const updateConversationRef = useRef(null);          // Latest updateConversation for new_message handler
  const addMessageRef = useRef(null);                  // Latest addMessage for new_message handler
  const setChatsRef = useRef(null);                    // Latest setChats for new_message handler
  const ingestRealtimeMessageRef = useRef(null);        // Latest ingestRealtimeMessage handler

  // ========================================================================
  // SECTION 3: USER ACCESS INFO
  // ========================================================================
  const accessInfo = getLivechatAccessInfo();
  const { role, accessLevel, platformAccountId } = accessInfo;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  const effectiveUserId = platformAccountId || user.id; // Super admin's ID for room naming

  // ========================================================================
  // SECTION 4: DATA FETCHING FUNCTIONS
  // ========================================================================

  /**
   * Fetch conversations list from API (paginated, 50 per page)
   * Respects permission-based filtering and optional filters (agent_ids, status_ids, date_from, date_to)
   * @param {number} [page] - Page number (1-based)
   * @param {{ agent_ids?: number[], status_ids?: number[], date_from?: string, date_to?: string }} [filterOverrides]
   * @param {{ windowState?: 'active'|'non-active'|null }} [options]
   */
  const fetchConversations = useCallback(async (page, filterOverrides, options = {}) => {
    const pageToFetch = typeof page === "number" && page >= 1 ? page : chatsPageRef.current;
    const effectiveFilters = filterOverrides ?? filtersRef.current;
    const windowState = options.windowState || null;
    try {
      setLoading(true);
      setAccessDenied(false);

      const response = await getLivechatConversations(selectedPhoneId, {
        limit: CHATS_PAGE_SIZE,
        page: pageToFetch,
        agent_ids: effectiveFilters.agent_ids?.length ? effectiveFilters.agent_ids : undefined,
        status_ids: effectiveFilters.status_ids?.length ? effectiveFilters.status_ids : undefined,
        date_from: effectiveFilters.date_from || undefined,
        date_to: effectiveFilters.date_to || undefined,
        window_state: windowState || undefined,
      });

      if (response.data.success) {
        const total = response.data.total ?? 0;
        setChatsTotal(total);
        setChatsPage(pageToFetch);
        chatsPageRef.current = pageToFetch;
        const counts = response.data.window_counts || {};
        setChatsWindowCounts({
          active: Number(counts.active ?? 0),
          non_active: Number(counts.non_active ?? 0),
        });

        const formattedChats = (response.data.data || []).map((chat) => {
          const lastMessageAt = chat.last_message?.sent_at || chat.last_message_at;
          return {
            id: `${chat.customer_phone}_${chat.phone_number_id}`,
            customer_phone: chat.customer_phone,
            phone_number_id: chat.phone_number_id,
            name: chat.display_name || chat.customer_name || chat.customer_phone,
            customer_name: chat.customer_name || null,
            contact_id: chat.contact_id || null,
            is_saved_contact: !!chat.is_saved_contact,
            phone: chat.customer_phone,
            lastMessage: formatLastMessageText(chat.last_message),
            timestamp: formatTimestampForDisplayUTC(lastMessageAt),
            unread: chat.unread_count || 0,
            is_window_open: chat.is_window_open !== undefined ? chat.is_window_open : true,
            last_message: chat.last_message || null,
            last_message_at: lastMessageAt,
            assigned_agent_id: chat.assigned_agent_id,
            assigned_agent_name: chat.assigned_agent_name,
            assigned_at: chat.assigned_at,
            status_id: chat.status_id ?? null,
            status_name: chat.status_name ?? null,
            status_color: chat.status_color ?? null,
            status_updated_at: chat.status_updated_at ?? null,
            agent_has_control: !!chat.agent_has_control,
            flow_paused_by_agent: !!chat.flow_paused_by_agent,
          };
        });

        formattedChats.sort((a, b) => {
          const timeA = new Date(a.last_message_at || 0).getTime();
          const timeB = new Date(b.last_message_at || 0).getTime();
          return timeB - timeA;
        });

        setChats(formattedChats);
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      }
    } finally {
      setLoading(false);
    }
  }, [selectedPhoneId]);

  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  /**
   * Update a chat in the list and in selectedChat (e.g. after status change)
   */
  const updateChatInList = useCallback((chatId, updates) => {
    setChats((prev) =>
      prev.map((c) => (c.id === chatId ? { ...c, ...updates } : c))
    );
    setSelectedChat((prev) => {
      if (!prev || prev.id !== chatId) return prev;
      return { ...prev, ...updates };
    });
  }, []);

  const setPage = useCallback((page) => {
    const p = Math.max(1, parseInt(page, 10) || 1);
    chatsPageRef.current = p;
    setChatsPage(p);
    fetchConversations(p);
  }, [fetchConversations]);

  /**
   * Fetch messages for a specific conversation
   * Merges database messages with real-time messages (from socket cache)
   */
  const fetchMessages = useCallback(async (customerPhone, phoneNumberId) => {
    try {
      if (!customerPhone || !phoneNumberId) {
        console.error("Missing parameters for fetchMessages:", { customerPhone, phoneNumberId });
        return;
      }
      setMessagesLoading(true);
      setMessagesLoadingMore(false);
      setAccessDenied(false);
      messagesOffsetRef.current = 0;
      hasMoreMessagesRef.current = false;
      setHasMoreMessages(false);

      const response = await getLivechatMessages(
        customerPhone,
        phoneNumberId,
        MESSAGES_PAGE_SIZE,
        0
      );

      if (response.data.success) {
        const meta = response.data.data || {};
        const dbMessages = meta.messages || [];
        const total = Number(meta.total ?? dbMessages.length);
        setMessagesTotal(total);

        const normalizedDbMessages = dbMessages.map((msg) => ({
          ...msg,
          sent_at: normalizeTimestampToISO(msg.sent_at) || msg.sent_at,
        }));

        const chatKey = `${customerPhone}_${phoneNumberId}`;
        const realtimeMessages = realtimeMessagesRef.current.get(chatKey) || [];

        messagesOffsetRef.current = normalizedDbMessages.length;
        const hasMore = Boolean(meta.has_more) || messagesOffsetRef.current < total;
        hasMoreMessagesRef.current = hasMore;
        setHasMoreMessages(hasMore);

        setMessages(mergeAndSortMessages(normalizedDbMessages, realtimeMessages));

        // Drop realtime entries now present in DB so stale unsigned URLs
        // cannot override a later merge.
        if (realtimeMessages.length > 0) {
          const dbKeys = new Set(
            normalizedDbMessages.map((m) => messageDedupeKey(m)).filter(Boolean)
          );
          const remaining = realtimeMessages.filter(
            (m) => !dbKeys.has(messageDedupeKey(m))
          );
          if (remaining.length > 0) {
            realtimeMessagesRef.current.set(chatKey, remaining);
          } else {
            realtimeMessagesRef.current.delete(chatKey);
          }
        }
        const contactUpdates = {
          contact_id: meta.contact_id ?? null,
          is_saved_contact: !!meta.is_saved_contact,
          customer_name: meta.customer_name ?? null,
          agent_has_control: !!meta.agent_has_control,
          flow_paused_by_agent: !!meta.flow_paused_by_agent,
        };
        if (meta.display_name) contactUpdates.name = meta.display_name;

        setChats((prevChats) =>
          prevChats.map((c) => {
            if (c.customer_phone === customerPhone && c.phone_number_id === phoneNumberId) {
              return {
                ...c,
                ...contactUpdates,
                unread: 0,
              };
            }
            return c;
          })
        );
        setSelectedChat((prev) => {
          if (!prev || prev.customer_phone !== customerPhone || prev.phone_number_id !== phoneNumberId) {
            return prev;
          }
          return { ...prev, ...contactUpdates };
        });
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
        setMessages([]);
        setHasMoreMessages(false);
        hasMoreMessagesRef.current = false;
      }
    } finally {
      setMessagesLoading(false);
    }
  }, []);

  /**
   * Load older messages when user scrolls to the top of the thread.
   */
  const loadMoreMessages = useCallback(async () => {
    const chat = selectedChatRef.current;
    if (
      !chat ||
      loadingMoreMessagesRef.current ||
      messagesLoading ||
      !hasMoreMessagesRef.current
    ) {
      return false;
    }

    loadingMoreMessagesRef.current = true;
    setMessagesLoadingMore(true);

    try {
      const offset = messagesOffsetRef.current;
      const response = await getLivechatMessages(
        chat.customer_phone,
        chat.phone_number_id,
        MESSAGES_PAGE_SIZE,
        offset
      );

      if (!response.data.success) return false;

      const meta = response.data.data || {};
      const dbMessages = (meta.messages || []).map((msg) => ({
        ...msg,
        sent_at: normalizeTimestampToISO(msg.sent_at) || msg.sent_at,
      }));

      if (dbMessages.length === 0) {
        hasMoreMessagesRef.current = false;
        setHasMoreMessages(false);
        return false;
      }

      const total = Number(meta.total ?? messagesTotal);
      setMessagesTotal(total);
      messagesOffsetRef.current = offset + dbMessages.length;
      const hasMore = Boolean(meta.has_more) || messagesOffsetRef.current < total;
      hasMoreMessagesRef.current = hasMore;
      setHasMoreMessages(hasMore);

      setMessages((prev) => mergeAndSortMessages(dbMessages, prev));
      return true;
    } catch (error) {
      console.error("Error loading more messages:", error);
      return false;
    } finally {
      loadingMoreMessagesRef.current = false;
      setMessagesLoadingMore(false);
    }
  }, [messagesLoading, messagesTotal]);

  // ========================================================================
  // SECTION 5: STATE UPDATE FUNCTIONS
  // ========================================================================

  /**
   * Update conversation in chats list when new message arrives
   * Updates: last message, unread count, window status, customer name
   */
  const updateConversation = useCallback((messageData) => {
    const customerPhone = getCustomerPhone(messageData);
    const phoneNumberId = normalizePhoneId(messageData.phone_number_id);
    
    // Only update conversations for the currently selected phoneId
    if (selectedPhoneId && !phonesMatch(phoneNumberId, selectedPhoneId)) {
      return;
    }
    
    const chatId = `${customerPhone}_${phoneNumberId}`;
    const isSelectedChat = selectedChat &&
      customersMatch(selectedChat.customer_phone, customerPhone) &&
      phonesMatch(selectedChat.phone_number_id, phoneNumberId);
    
    setChats((prevChats) => {
      const existingIndex = prevChats.findIndex(
        (chat) => chat.customer_phone === customerPhone && chat.phone_number_id === phoneNumberId
      );

      const lastMessageAt = messageData.sent_at || new Date().toISOString();
      
      // Use unread_count from socket event if available (from database)
      // Otherwise calculate locally (fallback for backward compatibility)
      let unreadCount = 0;
      if (messageData.unread_count !== undefined && messageData.unread_count !== null) {
        // Use unread_count from backend (database value)
        unreadCount = messageData.unread_count;
      } else {
        // Fallback: Calculate locally (for backward compatibility)
        if (existingIndex >= 0) {
          unreadCount = prevChats[existingIndex].unread || 0;
          if (messageData.direction === "inbound" && !isSelectedChat) {
            unreadCount += 1;
          }
        } else {
          unreadCount = messageData.direction === "inbound" ? 1 : 0;
        }
      }
      
      // Determine the display name.
      // - The server resolves and sends `display_name` (saved name > profile
      //   name > phone), so prefer it when present — this covers conversations
      //   that aren't currently loaded in the sidebar.
      // - For a saved contact, never let an inbound WhatsApp profile name
      //   overwrite the saved name.
      const existingChat = existingIndex >= 0 ? prevChats[existingIndex] : null;
      const contactId =
        messageData.contact_id ?? existingChat?.contact_id ?? null;
      const isSavedContact =
        messageData.is_saved_contact ?? existingChat?.is_saved_contact ?? false;
      const waProfileName =
        messageData.customer_name || existingChat?.customer_name || null;
      let customerName;
      if (messageData.display_name) {
        customerName = messageData.display_name;
      } else if (isSavedContact && existingChat?.name) {
        customerName = existingChat.name;
      } else if (existingChat) {
        customerName = waProfileName || existingChat.name || customerPhone;
      } else {
        customerName = waProfileName || customerPhone;
      }
      
      // Determine window status
      let windowStatus;
      if (existingIndex >= 0) {
        windowStatus = messageData.direction === "inbound" 
          ? true 
          : prevChats[existingIndex].is_window_open;
      } else {
        windowStatus = true; // New conversation: window is open
      }
      
      // Preserve or update assigned_agent_id
      const assignedAgentId = messageData.assigned_agent_id !== undefined
        ? messageData.assigned_agent_id
        : (existingIndex >= 0 ? prevChats[existingIndex].assigned_agent_id : null);
      
      const formattedChat = {
        id: chatId,
        customer_phone: customerPhone,
        phone_number_id: phoneNumberId,
        name: customerName,
        customer_name: waProfileName,
        contact_id: contactId,
        is_saved_contact: isSavedContact,
        phone: customerPhone,
        lastMessage: formatLastMessageText({
          message_text: messageData.message_text,
          message_type: messageData.message_type,
          media_url: messageData.media_url,
          caption: messageData.caption || null,
          button: messageData.button || null,
          template_name: messageData.template_name,
          template_data: messageData.template_data,
        }),
        timestamp: formatTimestampForDisplayUTC(lastMessageAt),
        unread: unreadCount,
        is_window_open: windowStatus,
        last_message: {
          message_text: messageData.message_text,
          message_type: messageData.message_type,
          sent_at: lastMessageAt,
          media_url: messageData.media_url || null,
          mime_type: messageData.mime_type || null,
          caption: messageData.caption || null,
          button: messageData.button || null,
          template_name: messageData.template_name,
          template_data: messageData.template_data,
        },
        last_message_at: lastMessageAt,
        assigned_agent_id: assignedAgentId,
        sent_by_agent_id: messageData.sent_by_agent_id || null,
      };

      if (existingIndex >= 0) {
        // Update existing conversation - move to top
        const updated = [...prevChats];
        updated.splice(existingIndex, 1);
        return [formattedChat, ...updated];
      } else {
        // Add new conversation at the top
        return [formattedChat, ...prevChats];
      }
    });
  }, [selectedChat, selectedPhoneId]);

  /**
   * Update a chat's window status in the list (e.g. from checkWindowStatus API)
   * Used so ChatList sidebar shows correct yellow dot based on API source of truth
   */
  const updateChatWindowStatus = useCallback((customerPhone, phoneNumberId, isWindowOpen) => {
    setChats((prevChats) =>
      prevChats.map((c) => {
        if (c.customer_phone === customerPhone && c.phone_number_id === phoneNumberId) {
          return { ...c, is_window_open: isWindowOpen };
        }
        return c;
      })
    );
  }, []);

  /**
   * Add message to messages state (for currently selected chat)
   */
  const addMessage = useCallback((messageData) => {
    const customerPhone = getCustomerPhone(messageData);
    
    setMessages((prev) => {
      // Check if message already exists
      const exists = prev.some(msg => 
        msg.message_id === messageData.message_id || 
        (msg.id && msg.id === messageData.id)
      );
      
      if (exists) return prev;

      const newMessage = {
        ...messageData,
        customer_phone: customerPhone,
        sent_at: normalizeTimestampToISO(messageData.sent_at) || messageData.sent_at,
      };
      
      // Add and sort by sent_at ascending
      const updated = [...prev, newMessage];
      updated.sort((a, b) => {
        const timeA = getTimestampForSorting(a.sent_at || a.timestamp);
        const timeB = getTimestampForSorting(b.sent_at || b.timestamp);
        return timeA - timeB;
      });
      
      return updated;
    });
  }, []);

  /**
   * Update a specific message in messages state
   */
  const updateMessage = useCallback((messageId, updates) => {
    setMessages((prev) =>
      prev.map((msg) =>
        msg.message_id === messageId ? { ...msg, ...updates } : msg
      )
    );
  }, []);

  // ========================================================================
  // SECTION 6: CHAT SELECTION
  // ========================================================================

  /**
   * Select a chat and fetch its messages
   * Handles secure subscription for agents (subscribe_assigned_chat)
   * Only leaves/joins the specific chat room (not user room)
   */
  const selectChat = useCallback((chat) => {
    const socket = getSocket();
    const previousChat = selectedChatRef.current;
    
    setAccessDenied(false);
    
    // Leave previous chat room if switching chats (only if actually joined)
    if (previousChat && socket && effectiveUserId) {
      const previousRoomId = chatRoom(
        effectiveUserId,
        previousChat.phone_number_id,
        previousChat.customer_phone
      );

      if (joinedChatRoomsRef.current.has(previousRoomId)) {
        const mode = joinedChatRoomsRef.current.get(previousRoomId);
        const parsed = parseChatRoomId(previousRoomId);
        if (parsed) {
          if (mode === CHAT_JOIN_MODE.SUBSCRIBE) {
            socket.emit(SOCKET_CLIENT_EVENTS.UNSUBSCRIBE_CHAT, {
              phoneNumberId: parsed.phoneNumberId,
              customerPhone: parsed.customerPhone,
            });
          } else {
            socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_CHAT, {
              userId: parsed.userId,
              phoneNumberId: parsed.phoneNumberId,
              customerPhone: parsed.customerPhone,
            });
          }
        }
        joinedChatRoomsRef.current.delete(previousRoomId);
      }
    }
    
    setSelectedChat(chat);
    selectedChatRef.current = chat;
    // Clear any in-flight reply staging — a draft reply only makes sense
    // within the chat it was started in.
    setReplyingTo(null);
    
    if (chat) {
      fetchMessages(chat.customer_phone, chat.phone_number_id);
      
      // Join chat room - use secure method for agents
      if (socket && effectiveUserId) {
        const newRoomId = chatRoom(
          effectiveUserId,
          chat.phone_number_id,
          chat.customer_phone
        );

        if (!joinedChatRoomsRef.current.has(newRoomId)) {
          if (role === "agent" && accessLevel === "own") {
            socket.emit(SOCKET_CLIENT_EVENTS.SUBSCRIBE_ASSIGNED_CHAT, {
              phoneNumberId: chat.phone_number_id,
              customerPhone: chat.customer_phone,
            });
            joinedChatRoomsRef.current.set(newRoomId, CHAT_JOIN_MODE.SUBSCRIBE);
          } else {
            socket.emit(SOCKET_CLIENT_EVENTS.JOIN_CHAT, {
              userId: effectiveUserId,
              phoneNumberId: chat.phone_number_id,
              customerPhone: chat.customer_phone,
            });
            joinedChatRoomsRef.current.set(newRoomId, CHAT_JOIN_MODE.JOIN);
          }
        }
      }
    } else {
      setMessages([]);
      messagesOffsetRef.current = 0;
      hasMoreMessagesRef.current = false;
      setHasMoreMessages(false);
      setMessagesTotal(0);
    }
  }, [fetchMessages, effectiveUserId, role, accessLevel]);

  // ========================================================================
  // SECTION 7: SOCKET ROOM MANAGEMENT
  // ========================================================================

  /**
   * Ingest a realtime message (socket or API confirmation).
   * Updates sidebar + message list when the chat is active. Dedupes by message_id.
   */
  const ingestRealtimeMessage = useCallback((messageData) => {
    const messageId = messageData.message_id || messageData.id;
    if (messageId && processedMessageIdsRef.current.has(messageId)) {
      return false;
    }

    if (messageId) {
      processedMessageIdsRef.current.add(messageId);
      if (processedMessageIdsRef.current.size > 1000) {
        const idsArray = Array.from(processedMessageIdsRef.current);
        processedMessageIdsRef.current = new Set(idsArray.slice(-500));
      }
    }

    const customerPhone = getCustomerPhone(messageData);
    const messagePhoneId = normalizePhoneId(messageData.phone_number_id);
    const currentPhoneId = normalizePhoneId(selectedPhoneIdRef.current);
    if (currentPhoneId && messagePhoneId !== currentPhoneId) {
      return false;
    }

    const normalizedMessage = {
      ...messageData,
      phone_number_id: messagePhoneId,
      customer_phone: customerPhone,
    };

    const updateConv = updateConversationRef.current;
    if (updateConv) updateConv(normalizedMessage);

    const chatKey = `${customerPhone}_${messagePhoneId}`;
    const realtimeMessages = realtimeMessagesRef.current.get(chatKey) || [];
    const messageExists = realtimeMessages.some(
      (msg) => msg.message_id === messageData.message_id
    );

    if (!messageExists) {
      let normalizedSentAt = normalizedMessage.sent_at;
      if (normalizedSentAt) {
        if (normalizedSentAt instanceof Date) {
          normalizedSentAt = normalizedSentAt.toISOString();
        } else if (typeof normalizedSentAt === "string") {
          const parsed = new Date(normalizedSentAt);
          if (!isNaN(parsed.getTime())) {
            normalizedSentAt = parsed.toISOString();
          }
        }
      }
      realtimeMessagesRef.current.set(chatKey, [
        ...realtimeMessages,
        {
          ...normalizedMessage,
          sent_at: normalizedSentAt || messageData.sent_at,
        },
      ]);
    }

    const currentSelectedChat = selectedChatRef.current;
    if (
      currentSelectedChat &&
      customersMatch(customerPhone, currentSelectedChat.customer_phone) &&
      phonesMatch(messagePhoneId, currentSelectedChat.phone_number_id)
    ) {
      const addMsg = addMessageRef.current;
      if (addMsg) addMsg(normalizedMessage);

      if (messageData.direction === "inbound") {
        markLivechatAsRead(customerPhone, messagePhoneId)
          .then((response) => {
            if (response?.data?.success) {
              const setChatsFn = setChatsRef.current;
              if (setChatsFn) {
                setChatsFn((prevChats) =>
                  prevChats.map((c) =>
                    customersMatch(c.customer_phone, customerPhone) &&
                    phonesMatch(c.phone_number_id, messagePhoneId)
                      ? { ...c, unread: 0 }
                      : c
                  )
                );
              }
            }
          })
          .catch((err) => console.error("Error marking as read:", err));
      }
    }

    return true;
  }, []);

  // Keep refs in sync for socket handlers (avoids stale closure, allows listener to be set up once)
  useEffect(() => {
    selectedPhoneIdRef.current = selectedPhoneId;
  }, [selectedPhoneId]);
  useEffect(() => {
    updateConversationRef.current = updateConversation;
    addMessageRef.current = addMessage;
    setChatsRef.current = setChats;
    ingestRealtimeMessageRef.current = ingestRealtimeMessage;
  }, [updateConversation, addMessage, setChats, ingestRealtimeMessage]);

  /**
   * Handle phone ID changes - clear state and leave previous user room
   * Must leave old user room before room effect runs so we stop receiving old-phone messages
   */
  useEffect(() => {
    if (selectedPhoneId) {
      const socket = getSocket();
      // Proactively leave previous user room when switching phones
      if (joinedUserRoomRef.current && socket && effectiveUserId) {
        const prev = joinedUserRoomRef.current;
        socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_USER_ROOM, { userId: prev.userId, phoneId: prev.phoneId });
        joinedUserRoomRef.current = null;
      }
      setChats([]);
      setSelectedChat(null);
      setMessages([]);
      setAccessDenied(false);
      setChatsPage(1);
      setChatsTotal(0);
      setChatsWindowCounts({ active: 0, non_active: 0 });
      chatsPageRef.current = 1;
      realtimeMessagesRef.current.clear();
      processedMessageIdsRef.current.clear();
      selectedChatRef.current = null;
      joinedChatRoomsRef.current.clear();
      setReplyingTo(null);
    }
  }, [selectedPhoneId, effectiveUserId]);

  /**
   * Manage Socket.IO room subscriptions based on access level
   * 
   * Super Admin / livechats.all:
   *   - Subscribe ONLY to user:{userId}:{phoneId} for selected phone
   *   - When switching phones: unsubscribe old, subscribe new
   * 
   * Agent with livechats.own:
   *   - Join livechat:{platform}:agent:{agentId} ONCE (account-wide, filtered)
   */
  useEffect(() => {
    const socket = getSocket();
    if (!socket || !effectiveUserId) return;

    const handleRoomSubscription = () => {
      // Agent with livechats.own: Join livechat room once
      if (role === 'agent' && accessLevel === 'own') {
        if (!livechatRoomJoinedRef.current) {
          socket.emit(SOCKET_CLIENT_EVENTS.JOIN_LIVECHAT_ROOM);
          livechatRoomJoinedRef.current = true;
          console.log(`👤 Agent joined livechat room (assigned chats only)`);
        }
        return;
      }

      // Super admin / livechats.all: Subscribe to selected phone's room only
      if (selectedPhoneId) {
        const currentUserRoom = { userId: effectiveUserId, phoneId: selectedPhoneId };
        const currentRoomKey = `${effectiveUserId}:${selectedPhoneId}`;
        
        // Unsubscribe from previous phone if switching (only if actually joined)
        if (joinedUserRoomRef.current && 
            joinedUserRoomRef.current.phoneId !== selectedPhoneId) {
          socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_USER_ROOM, { 
            userId: joinedUserRoomRef.current.userId, 
            phoneId: joinedUserRoomRef.current.phoneId 
          });
          console.log(`👋 Unsubscribed from previous phone room: ${joinedUserRoomRef.current.phoneId}`);
          joinedUserRoomRef.current = null;
        }

        // Subscribe to new phone's room (only if not already joined)
        if (!joinedUserRoomRef.current || 
            joinedUserRoomRef.current.phoneId !== selectedPhoneId) {
          socket.emit(SOCKET_CLIENT_EVENTS.JOIN_USER_ROOM, { 
            userId: effectiveUserId, 
            phoneId: selectedPhoneId 
          });
          console.log(`👤 Subscribed to user room for phone: ${selectedPhoneId}`);
          joinedUserRoomRef.current = currentUserRoom;
        }
      }
    };

    // Join immediately if already connected, and re-join on every connect (including reconnect)
    const onConnect = () => handleRoomSubscription();
    if (socket.connected) {
      handleRoomSubscription();
    }
    socket.on("connect", onConnect);

    // Cleanup: Only leave rooms that are actually joined
    return () => {
      if (socket && effectiveUserId) {
        socket.off("connect", onConnect);
        
        // Only leave user room if we actually joined it
        if (joinedUserRoomRef.current && (role !== 'agent' || accessLevel !== 'own')) {
          socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_USER_ROOM, { 
            userId: joinedUserRoomRef.current.userId, 
            phoneId: joinedUserRoomRef.current.phoneId 
          });
          joinedUserRoomRef.current = null;
        }
        
        // Only leave livechat room if we actually joined it
        if (role === 'agent' && accessLevel === 'own' && livechatRoomJoinedRef.current) {
          socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_LIVECHAT_ROOM);
          livechatRoomJoinedRef.current = false;
        }
      }
    };
  }, [selectedPhoneId, effectiveUserId, role, accessLevel]);

  // ========================================================================
  // SECTION 8: SOCKET EVENT HANDLERS
  // ========================================================================

  /**
   * Initialize socket and set up event listeners ONCE on mount
   * Uses refs for callbacks so we never remove/re-add new_message (avoids gap where events are missed)
   */
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    // Track successful room joins from server
    socket.on(SOCKET_EVENTS.JOINED_CHAT, ({ roomId }) => {
      if (roomId) {
        joinedChatRoomsRef.current.set(roomId, CHAT_JOIN_MODE.JOIN);
      }
    });

    socket.on(SOCKET_EVENTS.SUBSCRIBED_CHAT, ({ roomId }) => {
      if (roomId) {
        joinedChatRoomsRef.current.set(roomId, CHAT_JOIN_MODE.SUBSCRIBE);
      }
    });

    socket.on(SOCKET_EVENTS.JOINED_LIVECHAT, ({ roomId }) => {
      if (roomId && roomId.includes(":agent:")) {
        livechatRoomJoinedRef.current = true;
      }
    });

    const handleNewMessage = (messageData) => {
      ingestRealtimeMessageRef.current?.(messageData);
    };

    socket.on(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);

    socket.on(SOCKET_EVENTS.FLOW_CONTROL_CHANGED, (payload) => {
      if (!payload?.customer_phone || !payload?.phone_number_id) return;
      const chatId = `${payload.customer_phone}_${payload.phone_number_id}`;
      const hasControl = !!payload.agent_has_control;
      setChats((prev) =>
        prev.map((c) =>
          c.id === chatId
            ? {
                ...c,
                agent_has_control: hasControl,
                flow_paused_by_agent: hasControl,
              }
            : c
        )
      );
      setSelectedChat((prev) => {
        if (
          !prev ||
          prev.customer_phone !== payload.customer_phone ||
          prev.phone_number_id !== payload.phone_number_id
        ) {
          return prev;
        }
        return {
          ...prev,
          agent_has_control: hasControl,
          flow_paused_by_agent: hasControl,
        };
      });
    });

    socket.on(SOCKET_EVENTS.MESSAGE_STATUS_UPDATE, (statusUpdate) => {
      const currentSelectedChat = selectedChatRef.current;
      
      // Only update if this is for the selected chat
      if (currentSelectedChat && 
          statusUpdate.phone_number_id === currentSelectedChat.phone_number_id &&
          statusUpdate.recipient_wa_id === currentSelectedChat.customer_phone) {
        
        setMessages((prev) =>
          prev.map((msg) => {
            if (msg.message_id === statusUpdate.message_id) {
              const existingStatuses = msg.statuses || [];
              const statusExists = existingStatuses.some(
                (s) => s.status === statusUpdate.status && 
                       s.status_timestamp === statusUpdate.status_timestamp
              );
              
              const updatedStatuses = statusExists 
                ? existingStatuses 
                : [
                    ...existingStatuses,
                    {
                      status: statusUpdate.status,
                      status_timestamp: statusUpdate.status_timestamp,
                      error_code: statusUpdate.error_code || null,
                      error_title: statusUpdate.error_title || null,
                      error_message: statusUpdate.error_message || null,
                      error_details: statusUpdate.error_details || null,
                    }
                  ];
              
              return {
                ...msg,
                message_status: statusUpdate.status,
                statuses: updatedStatuses,
                error_code: statusUpdate.error_code || msg.error_code,
                error_title: statusUpdate.error_title || msg.error_title,
                error_message: statusUpdate.error_message || msg.error_message,
                error_details: statusUpdate.error_details || msg.error_details,
                read_at: statusUpdate.status === "read" 
                  ? statusUpdate.status_timestamp 
                  : msg.read_at,
              };
            }
            return msg;
          })
        );
      }
    });

    return () => {
      if (socket) {
        socket.off(SOCKET_EVENTS.NEW_MESSAGE, handleNewMessage);
        socket.off(SOCKET_EVENTS.FLOW_CONTROL_CHANGED);
        socket.off(SOCKET_EVENTS.MESSAGE_STATUS_UPDATE);
        socket.off(SOCKET_EVENTS.JOINED_CHAT);
        socket.off(SOCKET_EVENTS.SUBSCRIBED_CHAT);
        socket.off(SOCKET_EVENTS.JOINED_LIVECHAT);
      }
    };
    // Empty deps: set up ONCE on mount. Uses refs for callbacks so no stale closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========================================================================
  // SECTION 9: EFFECTS
  // ========================================================================

  /**
   * Fetch chat statuses once on mount (for dropdown and filters; do not refetch on chat change)
   */
  useEffect(() => {
    getLivechatStatuses()
      .then((res) => {
        if (res.data?.success && Array.isArray(res.data.data)) {
          setChatStatusesList(res.data.data);
        }
      })
      .catch((err) => console.error("Error fetching chat statuses:", err));
  }, []);

  /**
   * Initial fetch on phone change is driven by ChatList — it knows the
   * active sub-tab (active / non-active) and must always pass `windowState`
   * so `chatsTotal` matches the displayed list.
   *
   * We intentionally do NOT auto-fetch here without a windowState; doing so
   * raced with ChatList's tab-aware fetch and could overwrite `chatsTotal`
   * with the combined active+non-active count, breaking pagination.
   */

  /**
   * Cleanup: Leave all rooms when component unmounts only
   * Uses refs to track what's actually joined (prevents duplicate leaves)
   */
  useEffect(() => {
    return () => {
      const socket = getSocket();
      if (!socket || !effectiveUserId) return;

      // Leave all joined chat rooms (from ref, not state)
      joinedChatRoomsRef.current.forEach((mode, roomId) => {
        const parsed = parseChatRoomId(roomId);
        if (!parsed) return;
        if (mode === CHAT_JOIN_MODE.SUBSCRIBE) {
          socket.emit(SOCKET_CLIENT_EVENTS.UNSUBSCRIBE_CHAT, {
            phoneNumberId: parsed.phoneNumberId,
            customerPhone: parsed.customerPhone,
          });
        } else {
          socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_CHAT, {
            userId: parsed.userId,
            phoneNumberId: parsed.phoneNumberId,
            customerPhone: parsed.customerPhone,
          });
        }
      });
      joinedChatRoomsRef.current.clear();

      if (joinedUserRoomRef.current && (role !== "agent" || accessLevel !== "own")) {
        socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_USER_ROOM, {
          userId: joinedUserRoomRef.current.userId,
          phoneId: joinedUserRoomRef.current.phoneId,
        });
        joinedUserRoomRef.current = null;
      }

      if (role === "agent" && accessLevel === "own" && livechatRoomJoinedRef.current) {
        socket.emit(SOCKET_CLIENT_EVENTS.LEAVE_LIVECHAT_ROOM);
        livechatRoomJoinedRef.current = false;
      }
    };
    // Empty dependency array = only run on unmount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ========================================================================
  // SECTION 10: CONTEXT VALUE & PROVIDER
  // ========================================================================

  const value = {
    // State
    chats,
    selectedChat,
    messages,
    loading,
    messagesLoading,
    messagesLoadingMore,
    hasMoreMessages,
    messagesTotal,
    messagesPageSize: MESSAGES_PAGE_SIZE,
    selectedPhoneId,
    accessDenied,
    chatsTotal,
    chatsPage,
    chatsPageSize: CHATS_PAGE_SIZE,
    chatsWindowCounts,
    chatStatusesList,
    filters,
    replyingTo,

    // Access info
    role,
    accessLevel,

    // Actions
    fetchConversations,
    setPage,
    setFilters,
    fetchMessages,
    loadMoreMessages,
    selectChat,
    addMessage,
    updateConversation,
    updateChatWindowStatus,
    updateMessage,
    updateChatInList,
    setReplyingTo,
    ingestRealtimeMessage,
  };

  return <LiveChatContext.Provider value={value}>{children}</LiveChatContext.Provider>;
};

// ============================================================================
// CUSTOM HOOK
// ============================================================================

export const useLiveChat = () => {
  const context = useContext(LiveChatContext);
  if (!context) {
    throw new Error("useLiveChat must be used within LiveChatProvider");
  }
  return context;
};

export default LiveChatContext;
